import { Link, NavLink, Outlet, useMatch } from "react-router-dom";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { BookOpen, LibraryBig, Image as ImageIcon, Settings, Shield, Crown, Plus, LogOut, Moon, Sun, Monitor, Highlighter, Check, HelpCircle, Compass, Home, Rss, Mic, Aperture, Send, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NoteQuickAddSheet } from "@/components/notes/NoteQuickAddSheet";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useTheme } from "@/hooks/useTheme";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WorkspaceTabs } from "./WorkspaceTabs";
import { PersonaSwitcher } from "./PersonaSwitcher";
import { TierPreviewSwitcher } from "./admin/TierPreviewSwitcher";
import { BottomTabBar } from "./BottomTabBar";
import { UsageWarningBanner } from "./UsageWarningBanner";
import { TrialBanner } from "./TrialBanner";
import { LowCreditsBanner } from "./account/LowCreditsBanner";
import { PastDueBanner } from "./PastDueBanner";
import { PendingArchiveBanner } from "./PendingArchiveBanner";
import { TaxNoticeBanner } from "./banners/TaxNoticeBanner";
import { GlobalShortcutsDialog } from "./GlobalShortcutsDialog";
import { AnnouncementsBanner } from "./announcements/AnnouncementsBell";
import { UnifiedNotificationsBell } from "./notifications/UnifiedNotificationsBell";
import { HelpWidget } from "./support/HelpWidget";
import { PageShortcutsProvider } from "@/hooks/PageShortcutsProvider";
import { useFeedReaderUnreadCount } from "@/hooks/useFeedReaderUnreadCount";
import { useApplyFontPreferenceToBody } from "@/hooks/useFontPreference";
import { Badge } from "@/components/ui/badge";
import { composerCopy } from "@/lib/composer";

import { cn } from "@/lib/utils";
import type { Theme } from "@/lib/theme";

type NavItem = {
  to: string;
  label: string;
  icon: typeof BookOpen;
  end?: boolean;
  badgeKind?: "feedReaderUnread";
};

const primaryNav: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/my-library", label: "My Library", icon: LibraryBig },
  { to: "/excerpts", label: "My Excerpts", icon: BookOpen, end: true },
];

const secondaryNav: NavItem[] = [
  { to: "/image-studio", label: "Image Studio", icon: Aperture },
  { to: "/workspace-media", label: "Workspace Media", icon: ImageIcon },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/help", label: "Help", icon: HelpCircle },
];

export const AppShell = () => {
  useGlobalShortcuts();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const isMobile = useIsMobile();
  const { signOut } = useAuth();
  const { isAdmin, isSuperAdmin } = useUserRole();
  const { active } = useWorkspace();
  // Composer is universal across tiers — no flag/tier gate. See mem://features/composer-access-policy.md.
  const composerMatch = useMatch("/composer");
  const feedReaderMatch = useMatch("/feed-reader");
  const podcastsMatch = useMatch("/podcasts");
  const superAdminMatch = useMatch("/super-admin");
  // Pages that take the full viewport (no centered max-w container, no outer scroll).
  const composerFullWidth = composerMatch || feedReaderMatch || podcastsMatch;
  // Pages that get full width but keep normal scroll (no max-w cap).
  const fullWidthScroll = !!superAdminMatch;
  // When acting as the Super Admin Master Persona, scope nav down to platform-admin
  // surfaces only — workspace-scoped tools (Composer, Library, Excerpts, Readwise,
  // Feed Reader, Image Studio) are hidden. Dashboard + Settings remain so the user
  // can still navigate and switch personas. Role-based Admin / Super Admin nav
  // entries below are unaffected.
  const isSuperAdminWorkspace = active?.account_tier === "super_admin";
  const nav = (() => {
    if (isSuperAdminWorkspace) {
      return [
        { to: "/dashboard", label: "Dashboard", icon: Home },
        { to: "/settings", label: "Settings", icon: Settings },
      ] as NavItem[];
    }
    const items = [
      ...primaryNav,
      // Readwise sync is ungated across all tiers (2026-05-10).
      { to: "/readwise-highlights", label: "Readwise Highlights", icon: Highlighter, end: false },
      // My Feed Reader: available on every tier (per-workspace caps enforced server-side).
      { to: "/feed-reader", label: "My Feed Reader", icon: Rss, end: false, badgeKind: "feedReaderUnread" },
      // My Podcasts: mirrors My Feed Reader's per-workspace cap model. See mem://features/podcasts-v1-spec.md.
      { to: "/podcasts", label: "My Podcasts", icon: Mic, end: false },
      // Composer + Publisher sit between My Podcasts and Image Studio. Composer is universal across tiers.
      { to: "/composer", label: composerCopy.navLabel, icon: Compass, end: false },
      { to: "/publisher", label: "Publisher", icon: Send, end: false },
      ...secondaryNav,
    ];

    return items;
  })();
  const { theme, setTheme, resolved } = useTheme();
  // Sync the resolved Sans/Serif preference to a body class so the global
  // --app-font-family variable picks up the right family.
  useApplyFontPreferenceToBody();

  const themeOptions: { value: Theme; icon: typeof Sun; label: string }[] = [
    { value: "light", icon: Sun, label: "Light theme" },
    { value: "system", icon: Monitor, label: "System theme" },
    { value: "dark", icon: Moon, label: "Dark theme" },
  ];

  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("appshell.nav.collapsed") === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("appshell.nav.collapsed", navCollapsed ? "1" : "0");
  }, [navCollapsed]);

  const cycleTheme = () => {
    const order: Theme[] = ["light", "system", "dark"];
    const idx = order.indexOf(theme);
    setTheme(order[(idx + 1) % order.length]);
  };
  const ThemeCycleIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  const navItemClass = (isActive: boolean) =>
    cn(
      "flex items-center rounded-md text-sm font-medium transition-colors",
      navCollapsed ? "h-10 w-10 mx-auto justify-center" : "gap-3 px-[14px] py-2",
      isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "hover:bg-sidebar-accent/60"
    );

  const maybeTooltip = (key: string, label: string, node: ReactNode) =>
    navCollapsed ? (
      <Tooltip key={key}>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    ) : (
      <Fragment key={key}>{node}</Fragment>
    );

  return (
    <PageShortcutsProvider>
    <TooltipProvider delayDuration={200}>
    <div className="app-shell h-full min-h-0 flex w-full bg-background overflow-hidden">
      <aside
        className={cn(
          "relative hidden md:flex shrink-0 min-h-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground flex-col transition-[width] duration-200",
          navCollapsed ? "w-14" : "w-60"
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setNavCollapsed((v) => !v)}
              aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
              className="absolute top-1/2 -translate-y-1/2 -right-3 z-20 h-6 w-6 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm border border-primary/20 hover:bg-primary/90 transition-colors"
            >
              {navCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{navCollapsed ? "Expand navigation" : "Collapse navigation"}</TooltipContent>
        </Tooltip>
        <div
          className={cn(
            "flex items-center gap-2 py-1.5 min-h-[36px]",
            navCollapsed ? "px-3 justify-center" : "px-5"
          )}
        >
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            <span
              role="img"
              aria-label="Excerptus logo"
              className="h-7 w-7 bg-primary inline-block shrink-0"
              style={{
                WebkitMaskImage: "url(/excerptus-logo.webp)",
                maskImage: "url(/excerptus-logo.webp)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
            {!navCollapsed && (
              <span className="font-serif text-2xl font-semibold text-primary truncate">Excerptus</span>
            )}
          </Link>
        </div>
        <nav className={cn("flex-1 min-h-0 overflow-y-auto flex flex-col", navCollapsed ? "px-2 py-4 items-center justify-evenly" : "px-3 py-2 justify-evenly")}>
          {nav.map(({ to, label, icon: Icon, end, badgeKind }) => {
            // Help opens in a new tab so users don't lose their working page
            // (Composer draft, excerpt editor, etc.). Active state is omitted
            // because the current tab never navigates to /help.
            if (to === "/help") {
              return maybeTooltip(
                to,
                `${label} (opens in new tab)`,
                <a
                  href="/help"
                  target="_blank"
                  rel="noopener"
                  aria-label="Help (opens in new tab)"
                  className={navItemClass(false)}
                >
                  <Icon className="h-4 w-4" />
                  {!navCollapsed && <span className="flex-1">{label}</span>}
                  {!navCollapsed && (
                    <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />
                  )}
                </a>
              );
            }
            return maybeTooltip(
              to,
              label,
              <NavLink
                to={to}
                end={Boolean(end)}
                className={({ isActive }) => navItemClass(isActive)}
              >
                <Icon className="h-4 w-4" />
                {!navCollapsed && <span className="flex-1">{label}</span>}
                {!navCollapsed && badgeKind === "feedReaderUnread" && <FeedReaderUnreadBadge />}
              </NavLink>
            );
          })}
          {isAdmin &&
            maybeTooltip(
              "admin",
              "Admin",
              <NavLink to="/admin" className={({ isActive }) => navItemClass(isActive)}>
                <Shield className="h-4 w-4" /> {!navCollapsed && "Admin"}
              </NavLink>
            )}
          {isSuperAdmin &&
            maybeTooltip(
              "super-admin",
              "Super Admin",
              <NavLink to="/super-admin" className={({ isActive }) => navItemClass(isActive)}>
                <Crown className="h-4 w-4" /> {!navCollapsed && "Super Admin"}
              </NavLink>
            )}
        </nav>

        <div className={cn("shrink-0 space-y-2 border-t border-sidebar-border", navCollapsed ? "p-2" : "p-3")}>
          <div className={cn(!navCollapsed && "pb-2 mb-1 border-b border-sidebar-border/60")}>
            <PersonaSwitcher collapsed={navCollapsed} />
          </div>
          {navCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              {maybeTooltip(
                "theme",
                `Theme: ${theme}`,
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={cycleTheme} aria-label="Cycle theme">
                  <ThemeCycleIcon className="h-4 w-4" />
                </Button>
              )}
              {!isMobile && <UnifiedNotificationsBell />}
              {maybeTooltip(
                "shortcuts",
                "Keyboard shortcuts",
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShortcutsOpen(true)} aria-label="Keyboard shortcuts">
                  <HelpCircle className="h-4 w-4" />
                </Button>
              )}
              {maybeTooltip(
                "signout",
                "Sign out",
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={signOut} aria-label="Sign out">
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <div className="flex flex-1 items-center gap-0.5 rounded-md border border-sidebar-border p-0.5">
                {themeOptions.map(({ value, icon: Icon, label }) => (
                  <Button
                    key={value}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex-1 h-7 px-0",
                      theme === value && "bg-sidebar-accent text-sidebar-accent-foreground"
                    )}
                    onClick={() => setTheme(value)}
                    aria-label={label}
                    aria-pressed={theme === value}
                    title={label}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </Button>
                ))}
              </div>
              {!isMobile && <UnifiedNotificationsBell />}
              <Button variant="ghost" size="sm" onClick={() => setShortcutsOpen(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">
                <HelpCircle className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={signOut} aria-label="Sign out" title="Sign out">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <Link to="/dashboard" className="flex items-center gap-2 font-serif text-xl font-semibold text-primary">
            <span
              role="img"
              aria-label="Excerptus logo"
              className="h-6 w-6 bg-primary inline-block"
              style={{
                WebkitMaskImage: "url(/excerptus-logo.webp)",
                maskImage: "url(/excerptus-logo.webp)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
            Excerptus
          </Link>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setQuickNoteOpen(true)}
              aria-label="New note"
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
                resolved === "dark"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-blue-600 text-white hover:bg-blue-700",
              )}
            >
              <Plus className="h-5 w-5" />
            </button>
            {isMobile && <UnifiedNotificationsBell />}
          </div>
        </header>
        <NoteQuickAddSheet
          workspaceId={active?.id ?? null}
          open={quickNoteOpen}
          onOpenChange={setQuickNoteOpen}
        />
        {isSuperAdmin && (
          <div className="flex flex-wrap items-center justify-end gap-2 px-3 py-1 border-b border-border bg-card/40 sm:py-1.5">
            {active && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary sm:px-2.5 sm:text-xs">
                <span className="sm:hidden">Admin · {active.account_tier}</span>
                <span className="hidden sm:inline">Acting as: {active.workspace_label || active.display_name || "Untitled"} · {active.account_tier}</span>
              </span>
            )}
            <TierPreviewSwitcher />
          </div>
        )}
        {!isSuperAdminWorkspace && <WorkspaceTabs />}
        <TaxNoticeBanner />
        <AnnouncementsBanner />
        <PastDueBanner />
        <TrialBanner />
        <PendingArchiveBanner />
        <UsageWarningBanner />
        <LowCreditsBanner />
        <div id="app-scroll-container" className={composerFullWidth ? "flex-1 overflow-hidden pb-20 md:pb-0" : "flex-1 overflow-auto pb-20 md:pb-0"}>
          {composerFullWidth ? (
            <div className="h-full">
              <Outlet />
            </div>
          ) : fullWidthScroll ? (
            <div className="p-4 sm:p-6 md:p-8">
              <Outlet />
            </div>
          ) : (
            <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-8">
              <Outlet />
            </div>
          )}
        </div>
      </main>
      <HelpWidget />
      <BottomTabBar onOpenShortcuts={() => setShortcutsOpen(true)} />
      {!isMobile && <GlobalShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />}
    </div>
    </TooltipProvider>
    </PageShortcutsProvider>
  );
};

const FeedReaderUnreadBadge = () => {
  const { count } = useFeedReaderUnreadCount();
  if (count <= 0) return null;
  return (
    <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
      {count > 99 ? "99+" : count}
    </Badge>
  );
};
